import React from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import AppDownload from '../components/AppDownload';
import MarketingLayout from '../components/MarketingLayout';
import { blogArtFor } from '../lib/blogArt';
import {
  SITE_URL,
  blogAlternates,
  blogIndexPath,
  blogLangFromPath,
  blogPostPath,
  otherBlogLang,
} from '../lib/blogPaths';

/* ── Load all posts at build time ── */
const postModules = require.context('../content/posts', false, /\.json$/);
const POSTS_MAP = {};
postModules.keys().forEach((key) => {
  const post = postModules(key);
  if (post.slug) POSTS_MAP[post.slug] = post;
});

const formatDate = (iso, lang) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(lang === 'it' ? 'it-IT' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return null;
  }
};

const BlogPost = () => {
  const { slug } = useParams();
  // Language comes from the URL — /blog/<slug> is English, /it/blog/<slug>
  // Italian — so the two editions are separately indexable. See lib/blogPaths.js.
  const { pathname } = useLocation();
  const lang = blogLangFromPath(pathname);

  const post = POSTS_MAP[slug];

  /* Related posts: most shared tags first, then newest, topped up with
     other posts so the block is never half empty. Computed above the 404
     branch below so the hook order stays stable between renders. */
  const related = React.useMemo(() => {
    if (!post) return [];
    const tags = new Set((post.tags || []).map((t) => t.toLowerCase()));
    return Object.values(POSTS_MAP)
      .filter((p) => p.slug !== post.slug && (p[lang] || p.en)?.title)
      .map((p) => ({
        post: p,
        shared: (p.tags || []).filter((t) => tags.has(t.toLowerCase())).length,
      }))
      .sort(
        (a, b) =>
          b.shared - a.shared ||
          (b.post.created_at || '').localeCompare(a.post.created_at || '')
      )
      .slice(0, 3)
      .map((s) => s.post);
  }, [post, lang]);

  if (!post) {
    return (
      <MarketingLayout>
        <main className="bh-container" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 96, paddingBottom: 96 }}>
          <div style={{ textAlign: 'center' }}>
            <h1 style={{ fontSize: '2rem', marginBottom: 8 }}>404</h1>
            <p style={{ color: 'var(--muted)', marginBottom: 16 }}>
              {lang === 'it' ? 'Articolo non trovato.' : 'Post not found.'}
            </p>
            <Link to={blogIndexPath(lang)} style={{ color: 'var(--coral)', fontWeight: 600, textDecoration: 'none' }}>
              {lang === 'it' ? '← Torna al blog' : '← Back to blog'}
            </Link>
          </div>
        </main>
      </MarketingLayout>
    );
  }

  const localized = post[lang] || post['en'];
  const canonical = SITE_URL + blogPostPath(lang, slug);
  const dateline = formatDate(post.created_at, lang);

  // Auto-generated posts often repeat the title as the first markdown
  // heading — drop it so it doesn't render twice under the page <h1>.
  let body = localized.body || '';
  const firstLine = body.trimStart().split('\n', 1)[0].trim();
  if (/^#{1,2}\s/.test(firstLine) &&
      firstLine.replace(/^#{1,2}\s+/, '').toLowerCase() === localized.title.trim().toLowerCase()) {
    body = body.trimStart().split('\n').slice(1).join('\n');
  }

  return (
    <MarketingLayout>
      <Helmet>
        <html lang={lang} />
        <title>{localized.title} — Better Husband</title>
        <meta name="description" content={localized.meta_description} />
        <meta property="og:title" content={localized.title} />
        <meta property="og:description" content={localized.meta_description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        {post.hero_image?.url && <meta property="og:image" content={post.hero_image.url} />}
        <link rel="canonical" href={canonical} />
        {blogAlternates(slug).map((alt) => (
          <link key={alt.hrefLang} rel="alternate" hrefLang={alt.hrefLang} href={alt.href} />
        ))}
      </Helmet>

      <main className="bh-container" style={{ paddingTop: 48, paddingBottom: 96, flex: 1 }}>
        <article style={{ maxWidth: 760, margin: '0 auto' }}>
          <Link
            to={blogIndexPath(lang)}
            style={{ color: 'var(--coral)', fontWeight: 600, fontSize: 15, textDecoration: 'none', display: 'inline-block', marginBottom: 32 }}
          >
            {lang === 'it' ? '← Tutti gli articoli' : '← All posts'}
          </Link>

          <header style={{ marginBottom: 40 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
              {(post.tags || []).map((tag) => (
                <span key={tag} className="bh-tag">{tag}</span>
              ))}
            </div>
            <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.9rem)', marginBottom: 16 }}>
              {localized.title}
            </h1>
            {localized.subtitle && (
              <p className="bh-lead" style={{ marginBottom: 16 }}>{localized.subtitle}</p>
            )}
            <div style={{ fontSize: 14, color: 'var(--muted)' }}>
              {dateline}
              {post.reading_time_min &&
                ` · ${post.reading_time_min} min ${lang === 'it' ? 'di lettura' : 'read'}`}
              {post[otherBlogLang(lang)]?.body && (
                <>
                  {' · '}
                  <Link
                    to={blogPostPath(otherBlogLang(lang), slug)}
                    hrefLang={otherBlogLang(lang)}
                    style={{ color: 'var(--coral)' }}
                  >
                    {lang === 'it' ? 'Read in English' : 'Leggi in italiano'}
                  </Link>
                </>
              )}
            </div>
          </header>

          <figure style={{ marginBottom: 40 }}>
              <img
                src={blogArtFor(post).url}
                alt={blogArtFor(post).alt || localized.title}
                style={{ width: '100%', aspectRatio: '400 / 210', objectFit: 'cover', borderRadius: 'var(--radius-card)' }}
              />
              {post.hero_image?.credit && (
                <figcaption style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8, textAlign: 'right' }}>
                  Photo by{' '}
                  <a
                    href={`${post.hero_image.credit_url}?utm_source=betterhusband&utm_medium=referral`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: 'underline' }}
                  >
                    {post.hero_image.credit}
                  </a>{' '}
                  on Unsplash
                </figcaption>
              )}
          </figure>

          <div className="bh-prose" style={{ margin: '0 auto' }}>
            <ReactMarkdown
              components={{
                // The content generator scrapes arbitrary images from its
                // research sources (stock infographics, Instagram crawler
                // URLs that often 404). They're off-brand and break layout,
                // so body images are not rendered — only the curated
                // hero_image above the article is shown.
                img: () => null,
                // Posts link to each other in the body. A plain <a> would
                // full-page-reload out of the SPA and lose the language
                // the reader is on, so internal links go via the router.
                a: ({ href = '', children }) =>
                  href.startsWith('/') ? (
                    <Link to={href} style={{ color: 'var(--coral)' }}>
                      {children}
                    </Link>
                  ) : (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--coral)' }}
                    >
                      {children}
                    </a>
                  ),
              }}
            >{body}</ReactMarkdown>
          </div>

          {/* Related reading */}
          {related.length > 0 && (
            <section style={{ marginTop: 64 }}>
              <h2 style={{ fontSize: '1.4rem', marginBottom: 20 }}>
                {lang === 'it' ? 'Da leggere dopo' : 'Related reading'}
              </h2>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
                  gap: 20,
                }}
              >
                {related.map((r) => {
                  const rl = r[lang] || r.en;
                  const art = blogArtFor(r);
                  return (
                    <Link
                      key={r.slug}
                      to={blogPostPath(lang, r.slug)}
                      className="bh-card"
                      style={{
                        overflow: 'hidden',
                        textDecoration: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <img
                        src={art.url}
                        alt={art.alt || rl.title}
                        loading="lazy"
                        style={{ width: '100%', height: 120, objectFit: 'cover' }}
                      />
                      <div style={{ padding: '16px 18px' }}>
                        <h3 style={{ fontSize: '1rem', margin: 0, lineHeight: 1.35 }}>
                          {rl.title}
                        </h3>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* CTA */}
          <div
            className="bh-card"
            style={{ marginTop: 64, padding: '48px 32px', textAlign: 'center', transform: 'none' }}
          >
            <h3 style={{ fontSize: '1.6rem', marginBottom: 8 }}>
              {lang === 'it' ? 'Pronto a iniziare?' : 'Ready to start?'}
            </h3>
            <p style={{ color: 'var(--muted)', fontSize: 17, marginBottom: 24 }}>
              {lang === 'it'
                ? 'Better Husband ti suggerisce un piccolo gesto ogni giorno. 7 giorni gratis.'
                : 'Better Husband gives you one small action every day. 7 days free.'}
            </p>
            <AppDownload lang={lang} />
          </div>
        </article>
      </main>
    </MarketingLayout>
  );
};

export default BlogPost;
